import React, { useState, useEffect, useRef, useCallback } from 'react';
import { colors } from '../utils/colors';
import { API_URLS } from '../Api/urls';
import { getToken } from '../Api/auth';
import type { Teacher, TeacherAttendanceRecord, ActiveTab } from '../utils/types';
import BottomNav from './BottomNav';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  teacher: Teacher;
  myAttendance: TeacherAttendanceRecord | null;
  onNavigate: (tab: ActiveTab) => void;
}

interface DashboardStats {
  my_attendance_percent: number;
  student_presence_percent: number;
  student_presence_month: string;
  total_teacher_days: number;
  teacher_present_days: number;
  total_student_days: number;
  student_present_days: number;
  total_students?: number;
  students_present_today?: number;
  total_teachers?: number;
  teachers_present_today?: number;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const BellIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
    stroke={colors.textMuted} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 01-3.46 0"/>
  </svg>
);

const PULL_THRESHOLD = 65;

// ─── Component ────────────────────────────────────────────────────────────────

const HomeScreen: React.FC<Props> = ({ teacher, myAttendance, onNavigate }) => {
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [statsLoading, setStatsLoading]     = useState(true);
  const [pullY, setPullY]                   = useState(0);
  const [refreshing, setRefreshing]         = useState(false);

  const scrollRef    = useRef<HTMLDivElement>(null);
  const touchStartY  = useRef(0);
  const isPulling    = useRef(false);

  const isSupervisor = teacher.role === 'supervisor';

  // ── Fetch dashboard stats ──────────────────────────────────────────────────
  const fetchDashboardStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const token = getToken();
      const response = await fetch(
        API_URLS.STAT_CARDS_DATA_HOME_SCREEN(Number(teacher.id)),
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setDashboardStats({
        my_attendance_percent:    data.my_attendance_percent,
        student_presence_percent: data.student_presence_percent,
        student_presence_month:   data.student_presence_month,
        total_teacher_days:       data.total_teacher_days,
        teacher_present_days:     data.teacher_present_days,
        total_student_days:       data.total_student_days,
        student_present_days:     data.student_present_days,
        total_students:           data.total_students,
        students_present_today:   data.students_present_today,
        total_teachers:           data.total_teachers,
        teachers_present_today:   data.teachers_present_today,
      });
    } catch (err) {
      console.error('Dashboard stats fetch error:', err);
      setDashboardStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [teacher.id]);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  // ── Pull-to-refresh handlers ───────────────────────────────────────────────
  const onTouchStart = (e: React.TouchEvent) => {
    if (scrollRef.current && scrollRef.current.scrollTop === 0) {
      touchStartY.current = e.touches[0].clientY;
      isPulling.current = true;
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!isPulling.current) return;
    const delta = e.touches[0].clientY - touchStartY.current;
    if (delta > 0) {
      // Elastic damping: slows pull resistance as it extends
      setPullY(Math.min(delta * 0.45, PULL_THRESHOLD + 24));
    } else {
      isPulling.current = false;
      setPullY(0);
    }
  };

  const onTouchEnd = async () => {
    if (!isPulling.current) return;
    isPulling.current = false;

    if (pullY >= PULL_THRESHOLD) {
      setPullY(0);
      setRefreshing(true);
      // Trigger SW update check in background
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistration().then((reg) => reg?.update());
      }
      await fetchDashboardStats();
      setRefreshing(false);
    } else {
      setPullY(0);
    }
  };

  // ── Derived display values ─────────────────────────────────────────────────
  const roleLabel = isSupervisor
    ? 'SUPERVISOR'
    : teacher.className
    ? `${teacher.className.toUpperCase()}${
        teacher.sectionName ? ` - SEC ${teacher.sectionName.toUpperCase()}` : ''
      }`
    : teacher.role === 'admin'
    ? 'ADMIN'
    : 'SUBJECT TEACHER';

  const myAttendancePct: number | null = dashboardStats != null
    ? dashboardStats.my_attendance_percent
    : myAttendance
    ? myAttendance.status === 'present' ? 96
      : myAttendance.status === 'late'  ? 78
      : 0
    : null;

  const studentPresencePct   = dashboardStats?.student_presence_percent ?? null;
  const studentPresenceMonth = dashboardStats?.student_presence_month
    ?? new Date().toLocaleString('en-IN', { month: 'long', year: 'numeric' });

  const teachersPresentToday = dashboardStats?.teachers_present_today ?? null;
  const totalTeachers        = dashboardStats?.total_teachers ?? null;
  const studentsPresentToday = dashboardStats?.students_present_today ?? null;
  const totalStudents        = dashboardStats?.total_students ?? null;

  // ── Shimmer skeleton ───────────────────────────────────────────────────────
  const shimmer: React.CSSProperties = {
    background: 'linear-gradient(90deg, #e0e0e0 25%, #efefef 50%, #e0e0e0 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.4s infinite',
    borderRadius: 8,
    display: 'inline-block',
  };

  // Pull indicator progress (0-1)
  const pullProgress = Math.min(pullY / PULL_THRESHOLD, 1);
  const indicatorSize = 28;
  const strokeWidth = 2.5;
  const radius = (indicatorSize - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  // ── Nav cards config ───────────────────────────────────────────────────────

  const isRegularTeacher =
    teacher.role === 'class_teacher' || teacher.role === 'subject_teacher';

  const myAttendanceCard = {
    tab: 'my-attendance' as ActiveTab,
    label: 'My attendance',
    delay: '0s',
    iconBg: '#EEE3F7',
    cardBg: '#F5EEFA',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke={colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <polyline points="16 11 18 13 22 9"/>
      </svg>
    ),
  };

  const classAttendanceCard = {
    tab: 'class-attendance' as ActiveTab,
    label: isSupervisor ? 'Class attendance' : 'Student attendance',
    delay: isRegularTeacher ? '0s' : '0.05s',
    iconBg: '#EEE3F7',
    cardBg: '#F5EEFA',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke={colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 00-3-3.87"/>
        <path d="M16 3.13a4 4 0 010 7.75"/>
      </svg>
    ),
  };

  const supervisorCard = {
    tab: 'all-teacher-attendance' as ActiveTab,
    label: 'Teacher attendance',
    delay: '0.1s',
    iconBg: '#EEE3F7',
    cardBg: '#F5EEFA',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke={colors.primary}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <line x1="19" y1="8" x2="19" y2="14"/>
          <line x1="22" y1="11" x2="16" y2="11"/>
        </svg>
      ),
  };

  const examMarksCard = {
    tab: 'exam-marks' as ActiveTab,
    label: 'Exam marks',
    delay: isSupervisor ? '0.15s' : isRegularTeacher ? '0.05s' : '0.1s',
    iconBg: '#EEE3F7',
    cardBg: '#F5EEFA',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke={colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/>
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
        <line x1="9" y1="8" x2="15" y2="8"/>
        <line x1="9" y1="12" x2="15" y2="12"/>
      </svg>
    ),
  };

  const navCards = isRegularTeacher
    ? [classAttendanceCard, examMarksCard]
    : isSupervisor
    ? [myAttendanceCard, classAttendanceCard, supervisorCard, examMarksCard]
    : [myAttendanceCard, classAttendanceCard, supervisorCard, examMarksCard];

  const comingSoonDelay = isRegularTeacher ? '0.1s' : '0.2s';

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{
      height: '100dvh',
      background: '#FDFCFE',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      <style>{`
        @keyframes shimmer {
          0%   { background-position:  200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>

      {/* ── Top bar ── */}
      <div style={{
        padding: '30px 20px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Avatar */}
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            background: isSupervisor ? '#7C3AED' : '#C4933A',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, fontWeight: 800, color: '#fff', flexShrink: 0,
            position: 'relative',
          }}>
            {teacher.name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('')}
            {isSupervisor && (
              <div style={{
                position: 'absolute', bottom: -2, right: -2,
                width: 18, height: 18, borderRadius: '50%',
                background: '#FFD700',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, border: '2px solid #fff',
              }}>
                ★
              </div>
            )}
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: colors.text, lineHeight: 1.2 }}>
              {teacher.name}
            </div>
            <div style={{
              fontSize: 11, fontWeight: 700,
              color: isSupervisor ? '#7C3AED' : colors.primary,
              letterSpacing: '0.5px', marginTop: 2,
            }}>
              {roleLabel}
            </div>
          </div>
        </div>

        <button style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 4,
        }}>
          <BellIcon />
        </button>
      </div>

      {/* ── Pull-to-refresh indicator ── */}
      <div style={{
        height: refreshing ? 48 : pullY > 0 ? pullY : 0,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: isPulling.current ? 'none' : 'height 0.25s ease',
      }}>
        {refreshing ? (
          /* Spinning circle when loading */
          <svg
            width={indicatorSize}
            height={indicatorSize}
            style={{ animation: 'spin 0.8s linear infinite' }}
            viewBox={`0 0 ${indicatorSize} ${indicatorSize}`}
          >
            <circle
              cx={indicatorSize / 2}
              cy={indicatorSize / 2}
              r={radius}
              fill="none"
              stroke={colors.primary}
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={circumference * 0.25}
              strokeLinecap="round"
            />
          </svg>
        ) : pullY > 0 ? (
          /* Progress circle while pulling */
          <svg
            width={indicatorSize}
            height={indicatorSize}
            viewBox={`0 0 ${indicatorSize} ${indicatorSize}`}
            style={{
              transform: `rotate(${pullProgress >= 1 ? 0 : -90}deg)`,
              transition: 'transform 0.2s ease',
            }}
          >
            <circle
              cx={indicatorSize / 2}
              cy={indicatorSize / 2}
              r={radius}
              fill="none"
              stroke="#e0d4f7"
              strokeWidth={strokeWidth}
            />
            <circle
              cx={indicatorSize / 2}
              cy={indicatorSize / 2}
              r={radius}
              fill="none"
              stroke={colors.primary}
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={circumference * (1 - pullProgress)}
              strokeLinecap="round"
              style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }}
            />
          </svg>
        ) : null}
      </div>

      {/* ── Scrollable body ── */}
      <div
        ref={scrollRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: 80,
        }}
      >

        {/* ── Stat cards ── */}
        <div style={{ padding: '0 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>

          {/* My attendance */}
          <div style={{ background: '#E0F5F3', borderRadius: 18, padding: '20px 18px' }}>
            {statsLoading ? (
              <>
                <div style={{ ...shimmer, width: 72, height: 36, marginBottom: 8 }} />
                <div style={{ ...shimmer, width: 90, height: 14, marginBottom: 6 }} />
                <div style={{ ...shimmer, width: 60, height: 12 }} />
              </>
            ) : (
              <>
                <div style={{ fontSize: 32, fontWeight: 600, color: colors.text }}>
                  {myAttendancePct != null ? `${Math.round(myAttendancePct)}%` : '-'}
                </div>
                <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 6, fontWeight: 500 }}>
                  My attendance
                </div>
              </>
            )}
          </div>

          {/* Student presence */}
          <div style={{ background: '#E9F5E0', borderRadius: 18, padding: '20px 18px' }}>
            {statsLoading ? (
              <>
                <div style={{ ...shimmer, width: 64, height: 36, marginBottom: 8 }} />
                <div style={{ ...shimmer, width: 80, height: 14, marginBottom: 6 }} />
                <div style={{ ...shimmer, width: 70, height: 12 }} />
              </>
            ) : (
              <>
                <div style={{ fontSize: 32, fontWeight: 600, color: colors.text }}>
                  {studentPresencePct != null ? `${Math.round(studentPresencePct)}%` : '-'}
                </div>
                <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 4, fontWeight: 500, lineHeight: 1.3 }}>
                  {isSupervisor ? 'Student presence' : 'Student presence'}
                  <div style={{ fontSize: 11, color: colors.textLight, fontWeight: 600, marginTop: 2 }}>
                    {studentPresenceMonth.toUpperCase()}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Total teachers present today - supervisors only */}
          {isSupervisor && (statsLoading || totalTeachers != null || teachersPresentToday != null) && (
            <div style={{ background: '#FFF1E0', borderRadius: 18, padding: '20px 18px' }}>
              {statsLoading ? (
                <>
                  <div style={{ ...shimmer, width: 72, height: 36, marginBottom: 8 }} />
                  <div style={{ ...shimmer, width: 110, height: 14, marginBottom: 6 }} />
                  <div style={{ ...shimmer, width: 60, height: 12 }} />
                </>
              ) : (
                <>
                  <div style={{ fontSize: 32, fontWeight: 600, color: colors.text }}>
                    {teachersPresentToday != null ? teachersPresentToday : '-'}
                    {totalTeachers != null && (
                      <span style={{ fontSize: 18, color: colors.textMuted, fontWeight: 500 }}>
                        {` / ${totalTeachers}`}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 4, fontWeight: 500, lineHeight: 1.3 }}>
                    Teachers present
                    <div style={{ fontSize: 11, color: colors.textLight, fontWeight: 600, marginTop: 2 }}>
                      TODAY
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Total students present today - supervisors only */}
          {isSupervisor && (
            <div style={{ background: '#E0ECFF', borderRadius: 18, padding: '20px 18px' }}>
              {statsLoading ? (
                <>
                  <div style={{ ...shimmer, width: 72, height: 36, marginBottom: 8 }} />
                  <div style={{ ...shimmer, width: 110, height: 14, marginBottom: 6 }} />
                  <div style={{ ...shimmer, width: 60, height: 12 }} />
                </>
              ) : (
                <>
                  <div style={{ fontSize: 32, fontWeight: 600, color: colors.text }}>
                    {studentsPresentToday != null ? studentsPresentToday : '-'}
                    {totalStudents != null && (
                      <span style={{ fontSize: 18, color: colors.textMuted, fontWeight: 500 }}>
                        {` / ${totalStudents}`}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 4, fontWeight: 500, lineHeight: 1.3 }}>
                    Students present
                    <div style={{ fontSize: 11, color: colors.textLight, fontWeight: 600, marginTop: 2 }}>
                      TODAY
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Navigation cards ── */}
        <div style={{ padding: '20px 20px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>

          {navCards.map((card) => (
            <button
              key={card.tab}
              onClick={() => onNavigate(card.tab)}
              style={{
                width: '100%', background: card.cardBg,
                border: `1.5px solid ${colors.borderLight}`,
                borderRadius: 16, padding: '18px 20px',
                display: 'flex', alignItems: 'center', gap: 16,
                cursor: 'pointer', textAlign: 'left',
                animation: `slideUp 0.3s ease ${card.delay} both`,
              }}
            >
              <div style={{
                width: 44, height: 44, borderRadius: 12, background: card.iconBg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {card.icon}
              </div>
              <span style={{ fontSize: 16, fontWeight: 700, color: colors.primaryDark }}>
                {card.label}
              </span>
            </button>
          ))}

          {/* Fine & Complaint - temporarily replaced by "Register complaint" (coming soon) */}
          {/*
          <button
            onClick={() => onNavigate('fine-complaint')}
            style={{
              width: '100%', background: '#F5EEFA',
              border: `1.5px solid ${colors.borderLight}`,
              borderRadius: 16, padding: '18px 20px',
              display: 'flex', alignItems: 'center', gap: 16,
              cursor: 'pointer', textAlign: 'left',
              animation: `slideUp 0.3s ease ${comingSoonDelay} both`,
            }}
          >
            <div style={{
              width: 44, height: 44, borderRadius: 12, background: '#EEE3F7',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                stroke={colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="12" y1="18" x2="12" y2="12"/>
                <line x1="9" y1="15" x2="15" y2="15"/>
              </svg>
            </div>
            <span style={{ fontSize: 16, fontWeight: 700, color: colors.primaryDark }}>
              Fine &amp; Complaint
            </span>
          </button>
          */}

          {/* Register complaint - coming soon */}
          <button
            disabled
            style={{
              width: '100%', background: '#F5EEFA',
              border: `1.5px solid ${colors.borderLight}`,
              borderRadius: 16, padding: '18px 20px',
              display: 'flex', alignItems: 'center', gap: 16,
              cursor: 'not-allowed', textAlign: 'left',
              animation: `slideUp 0.3s ease ${comingSoonDelay} both`,
            }}
          >
            <div style={{
              width: 44, height: 44, borderRadius: 12, background: '#FDE4DC',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                stroke="#D9534F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <line x1="17" y1="8" x2="22" y2="13"/>
                <line x1="22" y1="8" x2="17" y2="13"/>
              </svg>
            </div>
            <span style={{ fontSize: 16, fontWeight: 700, color: colors.primaryDark, flex: 1 }}>
              Register complaint
            </span>
            <span style={{
              background: '#FDE4DC',
              color: '#D9534F',
              fontSize: 11,
              fontWeight: 700,
              padding: '6px 12px',
              borderRadius: 999,
              lineHeight: 1.2,
              textAlign: 'center',
              flexShrink: 0,
            }}>
              coming<br />soon!
            </span>
          </button>

        </div>
      </div>

      {/* ── Bottom nav ── */}
      <BottomNav activeTab="home" onNavigate={onNavigate} />

    </div>
  );
};

export default HomeScreen;
